import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { styled } from 'twin.macro';

import {
  Flex,
  Label,
  Modal,
  Table,
  Button,
  Spinner,
  TableRow,
  Explainer,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  TextInput,
  TableActions,
  TableHeadCell
} from '@sopranodesign/pulse-components';
import { useQuery, useMutation } from '@sopranodesign/pulse-hooks';

import { selectSharePointSite, verifySharePointSite } from '../../api';

const StyledOuterScrollableContainer = styled.div`
  position: relative;
  max-height: 24em;
  overflow-y: auto;

  @media (max-width: 1024px) {
    max-height: 20em;
  }
`;

const StyledScrollableContainer = styled.div`
  display: block;
  width: 100%;
  overflow-x: auto;
  outline: none;
  position: relative;

  table {
    width: 100%;
    table-layout: fixed;
  }

  td {
    overflow-wrap: break-word;
    white-space: normal;
  }
`;

const FadingExplainer = styled(Explainer)`
  transition: opacity 1s ease-out;
  opacity: ${({ fading }) => (fading ? 0 : 1)};
`;

const AddSiteModal = ({ state }) => {
  const {
    setActiveModal,
    setSelectedSite,
    setTokenInvalid
  } = state;

  const [hasCheckedToken, setHasCheckedToken] = useState(false);

  const {
    data,
    isError,
    refetch,
    isFetching
  } = useQuery({
    queryKey: ['generative-ai/sharepoint/site'],
    enabled: false
  });

  useEffect(() => {
    if (!hasCheckedToken) {
      refetch()
        .then((result) => {
          const message = result?.error?.response?.data?.message;
          if (message === 'Token is invalid, please reconnect.') {
            setActiveModal(null);
            setTokenInvalid(true);
          }
        })
        .finally(() => {
          setHasCheckedToken(true);
        });
    }
  }, [refetch, setActiveModal, hasCheckedToken, setTokenInvalid]);

  const [verifiedSites, setVerifiedSites] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [fadeError, setFadeError] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const tableRef = useRef(null);

  const selectMutation = useMutation({
    mutationFn: selectSharePointSite,
    onSuccess: (res) => {
      setErrorMessage(null);
      setSelectedSite(res.data.site);
      setActiveModal('addFolder');
    },
    onError: (error) => {
      const backendMessage = error?.response?.data?.message || error?.message || '';
      const allowedMessages = ['Invalid data provided.'];
      const message = allowedMessages.includes(backendMessage)
        ? backendMessage
        : 'Something went wrong. Please contact support.';
      setErrorMessage(message);
      setFadeError(false);
      setTimeout(() => {
        setFadeError(true);
        setTimeout(() => setErrorMessage(null), 1000);
      }, 10000);
    }
  });

  const verifyMutation = useMutation({ mutationFn: verifySharePointSite });

  useEffect(() => {
    const verifySites = async () => {
      if (!data?.data || verifying) {
        return;
      }

      setVerifying(true);
      setVerifiedCount(0);

      try {
        const results = await Promise.all(
          data.data.map(async (site) => {
            if (site.is_personal_site) {
              return null;
            }

            try {
              const res = await verifyMutation.mutateAsync({
                site_id: site.id,
                site_name: site.name,
                is_personal_site: site.is_personal_site ? 1 : 0
              });

              setVerifiedCount((prev) => prev + 1);
              const totalFileCount = res.data.reduce((sum, folder) => sum + (folder.file_count || 0), 0);

              return totalFileCount > 0 ? { ...site, file_count: totalFileCount } : null;
            } catch {
              setVerifiedCount((prev) => prev + 1);
              return null;
            }
          })
        );

        setVerifiedSites(results.filter(Boolean));
      } finally {
        setVerifying(false);
      }
    };

    verifySites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data]);

  const filteredSites = useMemo(() => {
    if (verifying || !verifiedSites.length) {
      return [];
    }

    const term = (searchTerm || '').trim().toLowerCase();

    const filtered = verifiedSites.filter(site =>
      site.name.toLowerCase().includes(term) ||
      site.url.toLowerCase().includes(term)
    );

    return filtered.sort((a, b) => {
      const valA = sortBy === 'name' ? a.name.toLowerCase() : a.file_count || 0;
      const valB = sortBy === 'name' ? b.name.toLowerCase() : b.file_count || 0;

      return typeof valA === 'string'
        ? (sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA))
        : (sortAsc ? valA - valB : valB - valA);
    });
  }, [verifiedSites, searchTerm, sortBy, sortAsc, verifying]);

  const handleSelect = (site) => {
    selectMutation.mutate({
      id: site.id,
      name: site.name,
      is_personal_site: site.is_personal_site
    });
  };

  const handleKeyDown = (e) => {
    if (!filteredSites.length) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev + 1) % filteredSites.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev - 1 + filteredSites.length) % filteredSites.length);
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      handleSelect(filteredSites[focusedIndex]);
    }
  };

  const renderBody = () => {
    if (isFetching || verifying) {
      const total = Math.max(data?.data?.length || 0, 1);
      const percentage = Math.min(100, Math.round((verifiedCount / total) * 100));

      return (
        <Flex col center middle tall>
          <Spinner />
          <Paragraph mt>
            {verifying ? `Loading sites... (${percentage}%)` : 'Loading sites...'}
          </Paragraph>
        </Flex>
      );
    }

    if (isError) {
      return (
        <Flex col center middle tall>
          <Explainer type="danger" hideLabel>
            <Paragraph sm danger>
              Failed to load sites. Please try again later.
            </Paragraph>
          </Explainer>
        </Flex>
      );
    }

    return (
      <Flex col tall>
        <Flex col ySpaceSm grow>
          <Label htmlFor="Search">Search</Label>
          <TextInput
            id="Search"
            placeholder="Search by site name or URL..."
            onChange={e => setSearchTerm(e.target.value)}
          />

          {errorMessage && (
            <FadingExplainer type="danger" hideLabel fading={fadeError}>
              <Paragraph sm danger>{errorMessage}</Paragraph>
            </FadingExplainer>
          )}

          <StyledOuterScrollableContainer>
            <StyledScrollableContainer
              ref={tableRef}
              tabIndex="0"
              role="region"
              aria-label="Scrollable site list"
              onKeyDown={handleKeyDown}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeadCell style={{ cursor: 'pointer' }}>
                      <Button
                        icon={sortBy === 'name' && sortAsc ? 'chevronUp' : 'chevronDown'}
                        variant="inline"
                        colour="black"
                        onClick={() => {
                          setSortBy('name');
                          setSortAsc(prev => sortBy === 'name' ? !prev : true);
                        }}
                        semiBold
                      >
                        Site
                      </Button>
                    </TableHeadCell>
                    <TableHeadCell style={{ cursor: 'pointer' }}>
                      <Button
                        icon={sortBy === 'file_count' && sortAsc ? 'chevronUp' : 'chevronDown'}
                        variant="inline"
                        colour="black"
                        onClick={() => {
                          setSortBy('file_count');
                          setSortAsc(prev => sortBy === 'file_count' ? !prev : true);
                        }}
                        semiBold
                      >
                        Total Files
                      </Button>
                    </TableHeadCell>
                    <TableHeadCell style={{ width: '8rem', textAlign: 'right' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSites.length > 0 ? (
                    filteredSites.map((site, index) => (
                      <TableRow
                        key={site.id}
                        style={{
                          backgroundColor: index === focusedIndex ? '#f0f0f0' : 'transparent'
                        }}
                        aria-selected={index === focusedIndex}
                      >
                        <TableCell>{site.name}</TableCell>
                        <TableCell>{site.file_count}</TableCell>
                        <TableActions style={{ width: '8rem', textAlign: 'right' }}>
                          <Button
                            variant="secondary"
                            icon="eye"
                            colour="primary"
                            loading={selectMutation.isPending}
                            disabled={selectMutation.isPending}
                            onClick={() => handleSelect(site)}
                          >
                            Select
                          </Button>
                        </TableActions>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Paragraph>No sites match your search.</Paragraph>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledScrollableContainer>
          </StyledOuterScrollableContainer>
        </Flex>
        <Paragraph sm mt center>
          Only sites with supported files are shown.
        </Paragraph>
      </Flex>
    );
  };

  return (
    <Modal
      header="Add SharePoint Site"
      handleCancel={() => setActiveModal(null)}
      confirmText=""
      cancelText="Cancel"
      body={renderBody()}
    />
  );
};

AddSiteModal.propTypes = {
  state: PropTypes.shape({
    setActiveModal: PropTypes.func.isRequired,
    setSelectedSite: PropTypes.func.isRequired,
    setTokenInvalid: PropTypes.func.isRequired
  }).isRequired
};

export default AddSiteModal;